import { useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Modal,
  Row,
  Skeleton,
  Space,
  Tag,
  Tooltip,
} from 'antd'
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import type { StripeCardElementOptions } from '@stripe/stripe-js'
import {
  CreditCardOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { useToast } from '../contexts/ToastContext'
import { useAuthSession } from '../lib/session'
import { useClientDetail } from '@/features/client/hooks'
import {
  useBillingAccess,
  useBillingOverview,
  useCancelSubscriptionMutation,
  useUpdatePaymentMethodMutation,
} from '../hooks/useBilling'
import {
  findAdvicePrepSubscription,
  useCancelAdvicePrepMutation,
  useSubscribeAdvicePrepMutation,
  useSubscribeAdvicePrepNowMutation,
} from '@/features/adviceprep/hooks'
import { ApiRequestError } from '@/lib/httpClient'
import { ADVICEPREP_PLATFORM_NAME } from '@/api/billing'
import { ADVICEPREP_TRIAL_CONNECTIONS, formatAdvicePrepCurrency } from '@/features/adviceprep/pricing'
import AdvicePrepUpsellCard from '@/features/adviceprep/billing/AdvicePrepUpsellCard'
import AdvicePrepManageCard from '@/features/adviceprep/billing/AdvicePrepManageCard'

const statusTagColor = {
  active: 'green',
  trialing: 'gold',
  canceled: 'default',
  incomplete: 'volcano',
  past_due: 'volcano',
} as const

const stripeElementOptions: StripeCardElementOptions = {
  style: {
    base: {
      color: '#31325F',
      fontWeight: '300',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSize: '16px',
      '::placeholder': { color: '#CFD7E0' },
    },
    invalid: {
      color: '#cf1322',
    },
  },
}

function formatDate(value?: string | null, withTime = false) {
  if (!value) return 'N/A'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'N/A'
  return withTime
    ? date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
}

type PaymentFormProps = {
  loading: boolean
  onCancel: () => void
  onSubmit: (paymentId: string) => Promise<void>
}

function UpdatePaymentMethodForm({ loading, onCancel, onSubmit }: PaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{ number?: string; expiry?: string; cvc?: string }>({})

  const resetFieldError = (field: 'number' | 'expiry' | 'cvc') => {
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }))
  }

  const setStripeFieldError = (message: string) => {
    const lowerMessage = message.toLowerCase()
    if (lowerMessage.includes('card number') || lowerMessage.includes('number')) {
      setFieldErrors({ number: message })
      return
    }
    if (lowerMessage.includes('expiry')) {
      setFieldErrors({ expiry: message })
      return
    }
    if (lowerMessage.includes('cvc') || lowerMessage.includes('security code')) {
      setFieldErrors({ cvc: message })
      return
    }
    setModalError(message)
  }

  const handleUpdate = async () => {
    setModalError(null)
    setFieldErrors({})

    if (!stripe || !elements) {
      setModalError('Stripe is not ready. Please try again.')
      return
    }

    const cardNumber = elements.getElement(CardNumberElement)
    if (!cardNumber) {
      setModalError('Card input is not ready.')
      return
    }

    setSubmitting(true)
    try {
      const result = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumber,
      })

      if (result.error?.message) {
        setStripeFieldError(result.error.message)
        return
      }

      const paymentId = result.paymentMethod?.id
      if (!paymentId) {
        setModalError('Unable to create payment method. Please try again.')
        return
      }

      try {
        await onSubmit(paymentId)
      } catch (e) {
        setModalError(e instanceof Error ? e.message : 'Unable to update payment method')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-3">
      {modalError && <Alert type="error" showIcon message={modalError} />}

      <div className={`rounded-lg border p-3 ${fieldErrors.number ? 'border-red-500' : 'border-gray-200'}`}>
        <CardNumberElement
          options={stripeElementOptions}
          onChange={(event) => {
            if (event.complete || !event.error) resetFieldError('number')
            if (event.error?.message) setFieldErrors((prev) => ({ ...prev, number: event.error?.message }))
          }}
        />
        {fieldErrors.number && <div className="mt-2 text-sm text-red-600">{fieldErrors.number}</div>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className={`rounded-lg border p-3 ${fieldErrors.expiry ? 'border-red-500' : 'border-gray-200'}`}>
          <CardExpiryElement
            options={stripeElementOptions}
            onChange={(event) => {
              if (event.complete || !event.error) resetFieldError('expiry')
              if (event.error?.message) setFieldErrors((prev) => ({ ...prev, expiry: event.error?.message }))
            }}
          />
          {fieldErrors.expiry && <div className="mt-2 text-sm text-red-600">{fieldErrors.expiry}</div>}
        </div>
        <div className={`rounded-lg border p-3 ${fieldErrors.cvc ? 'border-red-500' : 'border-gray-200'}`}>
          <CardCvcElement
            options={stripeElementOptions}
            onChange={(event) => {
              if (event.complete || !event.error) resetFieldError('cvc')
              if (event.error?.message) setFieldErrors((prev) => ({ ...prev, cvc: event.error?.message }))
            }}
          />
          {fieldErrors.cvc && <div className="mt-2 text-sm text-red-600">{fieldErrors.cvc}</div>}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="primary" loading={loading || submitting} onClick={() => void handleUpdate()}>
          Update
        </Button>
      </div>
    </div>
  )
}

export default function Billing() {
  const { addToast } = useToast()
  const authSession = useAuthSession()
  const clientId = authSession?.currentClientId || ''
  const stripePk = (import.meta.env['VITE_STRIPE_PK'] as string | undefined) || ''
  const stripePromise = useMemo(() => (stripePk ? loadStripe(stripePk) : null), [stripePk])
  const { data: clientDetail, isLoading: clientDetailLoading } = useClientDetail(clientId, !!clientId)
  const selectedClientPlatformTypes = useMemo(() => {
    const normalized = (clientDetail?.platformType || [])
      .map((platformType) => Number(platformType))
      .filter((platformType) => Number.isFinite(platformType))

    return normalized.length > 0 ? normalized : authSession?.platformType
  }, [authSession?.platformType, clientDetail?.platformType])

  const {
    data: overview,
    isLoading: overviewLoading,
    isFetching: overviewFetching,
    isError: overviewIsError,
    error: overviewError,
    refetch: refetchOverview,
  } = useBillingOverview(clientId, {
    currentUserTypeId: authSession?.currentUserTypeId,
    currentClientTypeId: authSession?.currentClientTypeId,
    advisorId: authSession?.advisorId,
    platformType: selectedClientPlatformTypes,
  })
  const {
    data: billingAccess,
    isLoading: accessLoading,
    isFetching: accessFetching,
    isError: accessIsError,
    error: accessError,
    refetch: refetchAccess,
  } = useBillingAccess(clientId)
  const cancelSubscriptionMutation = useCancelSubscriptionMutation(clientId)
  const updatePaymentMethodMutation = useUpdatePaymentMethodMutation(clientId)
  const cancelAdvicePrepMutation = useCancelAdvicePrepMutation(clientId)

  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [cancelContext, setCancelContext] = useState<{
    subscriptionId: string
    platformName: string
    isAdvicePrep?: boolean
  } | null>(null)
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  // Holds the promo code as well as the intent: a bare flag would collect the
  // card and then subscribe without the code the customer entered. Subscribing
  // early from a trial takes the same detour when there is no card to charge.
  const [pendingSubscribe, setPendingSubscribe] = useState<
    { kind: 'subscribe'; promoCode: string } | { kind: 'subscribeNow' } | null
  >(null)

  const totalAmountText = useMemo(
    () =>
      `$${(overview?.amountDue ?? 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    [overview?.amountDue]
  )

  const openCancelModal = (subscriptionId: string, platformName: string, isAdvicePrep = false) => {
    setCancelContext({ subscriptionId, platformName, isAdvicePrep })
    setCancelModalOpen(true)
  }

  const confirmCancel = async () => {
    try {
      if (!cancelContext) return
      if (cancelContext.isAdvicePrep) {
        await cancelAdvicePrepMutation.mutateAsync()
      } else {
        await cancelSubscriptionMutation.mutateAsync({
          subscriptionId: cancelContext.subscriptionId,
        })
      }
      addToast(`Subscription for ${cancelContext.platformName} canceled`, 'warning')
      setCancelModalOpen(false)
      setCancelContext(null)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unable to cancel subscription'
      addToast(message, 'error')
    }
  }

  const subscribeAdvicePrepMutation = useSubscribeAdvicePrepMutation(clientId)

  const subscribeToAdvicePrep = async (promoCode: string) => {
    try {
      // A declined card resolves with isSuccessful false rather than throwing.
      // Toasting success here would tell the customer it worked and leave the
      // same CTA on screen for them to click again.
      const result = await subscribeAdvicePrepMutation.mutateAsync(promoCode ? { promoCode } : {})
      if (!result.isSuccessful) {
        addToast(result.error, 'error')
        return
      }
      addToast('AdvicePrep added to your subscription', 'success')
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Unable to subscribe to AdvicePrep', 'error')
    }
  }

  // The upsell CTA promises to subscribe, so collecting a card has to carry on
  // to the subscribe call rather than dropping the user back on the same button.
  // The card has already validated any promo code by this point.
  const startAdvicePrepSubscribe = (promoCode: string) => {
    if (overview?.hasPaymentMethod) {
      void subscribeToAdvicePrep(promoCode)
      return
    }
    setPendingSubscribe({ kind: 'subscribe', promoCode })
    setPaymentModalOpen(true)
  }

  const subscribeAdvicePrepNowMutation = useSubscribeAdvicePrepNowMutation(clientId)

  const subscribeToAdvicePrepNow = async () => {
    try {
      const result = await subscribeAdvicePrepNowMutation.mutateAsync()
      if (!result.isSuccessful) {
        addToast(result.error, 'error')
        return
      }
      addToast('Your free trial has ended and your AdvicePrep subscription is active', 'success')
    } catch (e) {
      // The overview can think a card is on file when Stripe has nothing it can charge.
      if (e instanceof ApiRequestError && e.code === 'payment_method_required') {
        setPendingSubscribe({ kind: 'subscribeNow' })
        setPaymentModalOpen(true)
        return
      }
      // A declined card leaves the trial running, so the message is all that changes.
      addToast(e instanceof Error ? e.message : 'Unable to subscribe to AdvicePrep', 'error')
    }
  }

  const startAdvicePrepSubscribeNow = async () => {
    if (overview?.hasPaymentMethod) {
      await subscribeToAdvicePrepNow()
      return
    }
    setPendingSubscribe({ kind: 'subscribeNow' })
    setPaymentModalOpen(true)
  }

  // Closing the modal always drops the pending subscribe intent. Leaving it set
  // would make an unrelated card update later on subscribe the client without
  // asking — the intent belongs to the flow that opened the modal, not to the
  // modal itself.
  const openPaymentModal = () => {
    setPendingSubscribe(null)
    setPaymentModalOpen(true)
  }

  const closePaymentModal = () => {
    setPendingSubscribe(null)
    setPaymentModalOpen(false)
  }

  const confirmUpdatePayment = async (paymentId: string) => {
    await updatePaymentMethodMutation.mutateAsync({ paymentId })
    addToast('Payment method updated', 'success')
    setPaymentModalOpen(false)

    if (pendingSubscribe) {
      const pending = pendingSubscribe
      setPendingSubscribe(null)
      if (pending.kind === 'subscribeNow') await subscribeToAdvicePrepNow()
      else await subscribeToAdvicePrep(pending.promoCode)
    }
  }

  const loading = overviewLoading || accessLoading || clientDetailLoading
  // Spec 5.3: AdvicePrep has no read endpoint. It is one of the rows the shared
  // overview already returns, picked out by price nickname.
  const advicePrepRow = findAdvicePrepSubscription(overview?.subscriptions)
  // An ended subscription has nothing left to manage; the paywall sends these
  // customers here to choose a plan, so they get the subscribe card instead.
  const advicePrepSubscription = advicePrepRow?.isExpired ? null : advicePrepRow
  const subscriptionCount = overview?.subscriptions.length ?? 0
  const hasClientSelected = !!clientId
  const hasBillingAccess = billingAccess?.hasBillingAccess ?? false

  return (
    <div className="relative overflow-hidden rounded-2xl border border-brand-100 shadow-card bg-gradient-to-br from-white via-white to-emerald-50/25">
      <div
        className="pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full bg-emerald-100/50 blur-2xl"
        aria-hidden
      />

      <div className="px-6 pt-5 pb-4 border-b border-brand-100 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Billing</h1>
          <p className="text-sm text-gray-500 mt-1">Subscription and payment method</p>
        </div>
        <Tooltip title="Refresh billing data">
          <Button
            icon={<ReloadOutlined />}
            aria-label="Refresh billing data"
            loading={overviewFetching || accessFetching}
            onClick={() => {
              refetchOverview()
              refetchAccess()
              addToast('Billing data refreshed', 'info')
            }}
          />
        </Tooltip>
      </div>

      <div className="p-6">
        {!hasClientSelected && (
          <Alert
            type="warning"
            showIcon
            className="mb-4"
            message="Select a client from the Clients page to manage billing."
          />
        )}

        {accessIsError && (
          <Alert
            type="error"
            showIcon
            className="mb-4"
            message={(accessError as Error)?.message || 'Failed to load billing access'}
          />
        )}
        {overviewIsError && (
          <Alert
            type="error"
            showIcon
            className="mb-4"
            message={(overviewError as Error)?.message || 'Failed to load billing subscriptions'}
          />
        )}
        {hasClientSelected && !loading && !hasBillingAccess && (
          <Alert
            type="warning"
            showIcon
            className="mb-4"
            message="You do not have billing access for the selected client."
          />
        )}

        {loading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : (
          <Row gutter={[20, 20]}>
            <Col xs={24} lg={12}>
              <Card className="h-full border border-brand-100 rounded-xl">
                <Space direction="vertical" size={18} className="w-full">
                  <div>
                    <div className="text-sm text-gray-500">Total Amount Due</div>
                    <div className="text-3xl font-bold text-gray-900 mt-1 inline-flex items-center gap-2">
                      <DollarOutlined className="text-brand-500" />
                      {totalAmountText}
                    </div>
                  </div>

                  <div>
                    <div className="text-base font-semibold text-gray-900 mb-3">
                      Your current subscription{subscriptionCount > 1 ? 's' : ''}
                    </div>
                    {subscriptionCount === 0 && (
                      <Alert type="info" showIcon message="No active subscriptions for this client." />
                    )}

                    <Space direction="vertical" size={12} className="w-full">
                      {overview?.subscriptions.map((subscription) =>
                        subscription.price.map((item) => {
                          const advicePrepEstimate =
                            item.platformName === ADVICEPREP_PLATFORM_NAME &&
                            (subscription.status === 'active' ||
                              (subscription.status === 'canceled' && !subscription.isExpired))
                              ? (subscription.upcomingInvoice ?? null)
                              : null
                          return (
                            <div
                              key={`${subscription.subscriptionId}-${item.platformName}`}
                              className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50/60 px-4 py-3"
                            >
                              <div>
                                <div className="font-semibold text-gray-900">
                                  {item.platformName === ADVICEPREP_PLATFORM_NAME && subscription.status === 'trialing'
                                    ? `AdvicePrep — ${ADVICEPREP_TRIAL_CONNECTIONS} connections free during trial`
                                    : item.platformName}
                                </div>
                                <div className="text-sm text-gray-700 mt-1">
                                  {/* `amount` is the discounted figure, which a trial
                                      renders as either $0.00 or the full fee depending
                                      on how the trial is implemented server-side.
                                      Neither reads correctly next to "free during
                                      trial", so state both halves explicitly. */}
                                  {subscription.status === 'trialing'
                                    ? `$0.00 now · $${item.fullAmount.toFixed(2)}/month from ${formatDate(subscription.trialEnd)}`
                                    : advicePrepEstimate
                                      ? formatAdvicePrepCurrency(advicePrepEstimate.total, 2)
                                      : `$${item.amount.toFixed(2)}`}
                                  <Tag className="ml-2" color={statusTagColor[subscription.status]}>
                                    {subscription.status}
                                  </Tag>
                                </div>
                                {subscription.status === 'active' && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Next billing date: {formatDate(subscription.nextBillingDate)}
                                    {advicePrepEstimate &&
                                      ` · Estimate: ${formatAdvicePrepCurrency(item.amount, 2)} flat fee plus connection usage${advicePrepEstimate.tax !== 0 ? ', incl. GST' : ''}`}
                                  </div>
                                )}
                                {subscription.status === 'past_due' && (
                                  <div className="text-xs text-red-600 mt-1">
                                    Payment failed. Settle the outstanding invoice below to restore access.
                                  </div>
                                )}
                                {subscription.status === 'trialing' && subscription.trialEnd && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Trial ends: {formatDate(subscription.trialEnd)}
                                  </div>
                                )}
                                {subscription.status === 'canceled' && !subscription.isExpired && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Subscription canceled. Service ends {formatDate(subscription.currentPeriodEnd)}
                                  </div>
                                )}
                                {subscription.status === 'canceled' && subscription.isExpired && (
                                  <div className="text-xs text-gray-500 mt-1">Subscription has been canceled.</div>
                                )}
                                {subscription.status === 'incomplete' && (
                                  <div className="text-xs text-amber-700 mt-1">
                                    Subscription incomplete. Add payment method to activate.
                                  </div>
                                )}
                              </div>

                              {(subscription.status === 'active' || subscription.status === 'trialing') && (
                                <Tooltip title="Cancel subscription">
                                  <Button
                                    danger
                                    icon={<StopOutlined />}
                                    aria-label={`Cancel ${item.platformName} subscription`}
                                    disabled={!hasBillingAccess}
                                    onClick={() =>
                                      openCancelModal(
                                        subscription.subscriptionId,
                                        item.platformName,
                                        item.platformName === ADVICEPREP_PLATFORM_NAME
                                      )
                                    }
                                  />
                                </Tooltip>
                              )}
                            </div>
                          )
                        })
                      )}
                    </Space>
                  </div>
                </Space>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card className="h-full border border-brand-100 rounded-xl">
                <div className="text-base font-semibold text-gray-900 mb-3">Payment Method</div>
                {/* Gated on the client having a Stripe customer, not a subscription: an
                    account between subscriptions still needs to see and fix its card. */}
                {overview ? (
                  <>
                    {overview.hasPaymentMethod ? (
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="Card Type">{overview.cardType?.toUpperCase()}</Descriptions.Item>
                        <Descriptions.Item label="Card">**** **** **** {overview.cardLast4}</Descriptions.Item>
                        <Descriptions.Item label="Billing Scheme">{overview.billingScheme}</Descriptions.Item>
                      </Descriptions>
                    ) : (
                      <Alert type="warning" showIcon message="No payment method on file" />
                    )}

                    <div className="mt-4 flex justify-end">
                      <Button
                        icon={<CreditCardOutlined />}
                        aria-label="Update payment method"
                        disabled={!hasBillingAccess}
                        onClick={openPaymentModal}
                      >
                        {overview.hasPaymentMethod ? 'Update Payment Method' : 'Add Payment Method'}
                      </Button>
                    </div>
                  </>
                ) : (
                  <Alert type="info" showIcon message="No billing account for this client yet." />
                )}
              </Card>
            </Col>

            {hasClientSelected && (
              <Col xs={24}>
                {advicePrepSubscription ? (
                  <AdvicePrepManageCard
                    clientId={clientId}
                    subscription={advicePrepSubscription}
                    cardLabel={
                      overview?.hasPaymentMethod
                        ? `${overview.cardType?.toUpperCase()} ending ${overview.cardLast4}`
                        : null
                    }
                    onUpdatePaymentMethod={openPaymentModal}
                    onSubscribeNow={startAdvicePrepSubscribeNow}
                    subscribingNow={subscribeAdvicePrepNowMutation.isPending}
                    disabled={!hasBillingAccess}
                  />
                ) : (
                  <AdvicePrepUpsellCard
                    hasPaymentMethod={!!overview?.hasPaymentMethod}
                    subscribing={subscribeAdvicePrepMutation.isPending}
                    onSubscribe={startAdvicePrepSubscribe}
                    onRequestPaymentMethod={startAdvicePrepSubscribe}
                    disabled={!hasBillingAccess}
                  />
                )}
              </Col>
            )}
          </Row>
        )}
      </div>

      <Modal
        open={cancelModalOpen}
        title="Cancel Subscription"
        okText="Cancel Subscription"
        okButtonProps={{ danger: true, loading: cancelSubscriptionMutation.isPending }}
        onCancel={() => setCancelModalOpen(false)}
        onOk={confirmCancel}
      >
        <p className="text-sm text-gray-700 inline-flex items-center gap-2">
          <ExclamationCircleOutlined className="text-amber-500" />
          Cancel {cancelContext?.platformName || 'this'} subscription?
        </p>
      </Modal>

      <Modal
        open={paymentModalOpen}
        title="Update Payment Method"
        footer={null}
        onCancel={closePaymentModal}
      >
        {!stripePk && (
          <Alert type="error" showIcon message="Missing VITE_STRIPE_PK. Configure Stripe publishable key." />
        )}
        {stripePk && stripePromise && (
          <Elements stripe={stripePromise}>
            <UpdatePaymentMethodForm
              loading={updatePaymentMethodMutation.isPending}
              onCancel={closePaymentModal}
              onSubmit={confirmUpdatePayment}
            />
          </Elements>
        )}
      </Modal>
    </div>
  )
}
